import { useState, useCallback } from "react";
import { Pressable, Text, Platform } from "react-native";
import { ChevronRight, Check } from "lucide-react-native";
import { EMBER } from "../../onboarding/theme";
import { useInterestCapture } from "../hooks/useInterestCapture";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "./ScreenTitle";
import { semanticColors } from "@louise/theme/tokens";

type Props = {
  featureId: string;
  featureType: "program" | "supplement" | "test" | "product" | "therapy";
  featureName: string;
  label?: string;
  capturedLabel?: string;
};

export default function InterestButton({
  featureId,
  featureType,
  featureName,
  label = "I'm interested",
  capturedLabel = "We'll notify you!",
}: Props) {
  const [captured, setCaptured] = useState(false);
  const { capture } = useInterestCapture();
  const capturePress = usePressCapture("InterestButton");

  const handlePress = useCallback(() => {
    capturePress(
      pressLabel(captured ? capturedLabel : label),
      "register_interest",
    );
    if (captured) {
      return;
    }
    capture({ featureId, featureType, featureName });
    setCaptured(true);
  }, [
    captured,
    capture,
    capturePress,
    capturedLabel,
    label,
    featureId,
    featureType,
    featureName,
  ]);

  return (
    <Pressable
      onPress={handlePress}
      style={
        {
          backgroundColor: captured
            ? semanticColors.status.successTint
            : EMBER.colors.warm,
          paddingVertical: 14,
          paddingHorizontal: 24,
          borderRadius: 12,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          overflow: "hidden",
          ...(Platform.OS === "web" &&
            !captured && {
              ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
            }),
        } as Record<string, unknown>
      }
    >
      {captured ? (
        <Check size={16} color={EMBER.colors.green} strokeWidth={2} />
      ) : (
        <ChevronRight
          size={16}
          color={semanticColors.text.inverse}
          strokeWidth={2}
        />
      )}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "700",
          color: captured ? EMBER.colors.green : semanticColors.text.inverse,
        }}
      >
        {captured ? capturedLabel : label}
      </Text>
    </Pressable>
  );
}
